import { useEffect, useRef, useState } from "react";
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Divider, IconButton, LinearProgress, MenuItem, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import UploadFileRounded from "@mui/icons-material/UploadFileRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SaveRounded from "@mui/icons-material/SaveRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import OpenInFullRounded from "@mui/icons-material/OpenInFullRounded";
import CloseFullscreenRounded from "@mui/icons-material/CloseFullscreenRounded";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import FindInPageOutlined from "@mui/icons-material/FindInPageOutlined";
import { listAiJobs, uploadAiFile, readAiJob, readAiResult, saveAiReview, retryAiJob, readAiSource, matchAiItems, readAiRegistrations, registerAiOrder } from "../../api/ossAiApi";
import { fetchOssStandards } from "../../api/ossApi";

const statuses = { QUEUED: "처리 대기", OCR_RUNNING: "문자 인식 중", LLM_RUNNING: "주문 정보 분석 중", REVIEW_REQUIRED: "검토 대기", FAILED: "처리 실패" };
const headerFields = { customer_po_no: "고객 발주번호", customer_name: "거래처", supplier_name: "공급자", order_date: "주문일", due_date: "요청 납기", delivery_site: "납품 장소", contact: "담당자", phone: "연락처", notes: "비고" };
const itemFields = { name: "품목명", specification: "규격", quantity: "수량", unit: "단위", due_date: "납기" };
const reasonLabels = { MISSING_REQUIRED: "필수값 누락", INVALID_FORMAT: "형식 확인", AMBIGUOUS_DATE: "날짜 확정", SOURCE_NOT_MATCHED: "원문 위치", LOW_OCR_CONFIDENCE: "OCR 인식", TEXT_ENCODING_ERROR: "문자 깨짐" };
const errorText = e => e?.response?.data?.message || "요청을 처리하지 못했습니다. 다시 시도해주세요.";
const running = status => ["QUEUED", "OCR_RUNNING", "LLM_RUNNING"].includes(status);

function ProcessingStatus({ job }) {
  const steps = [
    { title: "처리 대기", detail: "발주서를 접수했습니다." },
    { title: "문자 인식", detail: "원본의 텍스트와 표를 읽습니다." },
    { title: "주문 정보 분석", detail: "거래처, 품목, 수량과 납기를 정리합니다." },
    { title: "검토 준비", detail: "분석이 끝나면 주문 초안이 표시됩니다." },
  ];
  const active = job ? ["QUEUED", "OCR_RUNNING", "LLM_RUNNING"].indexOf(job.status) : -1;
  const title = !job ? "작업 상태를 확인하고 있어요" : active === 0 ? "발주서 분석을 준비하고 있어요" : active === 1 ? "발주서 내용을 읽고 있어요" : "주문 정보를 정리하고 있어요";
  return <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 540, px: { xs: 2, lg: 3 }, pt: 1, pb: 1 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 2, borderBottom: "1px solid #e7ebef" }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "#64717b" }}>
        <AutoAwesomeOutlined sx={{ fontSize: 18, color: "#147d70" }} />
        <Typography fontSize={12}>AI 문서 처리</Typography>
      </Stack>
      <Typography sx={{ fontSize: 12, color: "#7d8791", fontVariantNumeric: "tabular-nums" }}>
        {active >= 0 ? `${active + 1} / ${steps.length} 단계` : "상태 조회 중"}
      </Typography>
    </Stack>
    <Box sx={{ flex: 1, minHeight: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", py: 4 }}>
      <Box aria-hidden="true" sx={{ position: "relative", width: 72, height: 72, display: "grid", placeItems: "center", mb: 3 }}>
        <CircularProgress variant="determinate" value={100} size={72} thickness={1.5} sx={{ position: "absolute", inset: 0, color: "#e9efed" }} />
        <CircularProgress size={72} thickness={1.5} sx={{ position: "absolute", inset: 0, color: "#24947c", "@media (prefers-reduced-motion: reduce)": { animation: "none", "& .MuiCircularProgress-circle": { animation: "none" } } }} />
        <DescriptionOutlined sx={{ fontSize: 30, color: "#348676" }} />
      </Box>
      <Typography role="status" aria-live="polite" sx={{ fontSize: 22, fontWeight: 500, color: "#26323b", lineHeight: 1.5, wordBreak: "keep-all", overflowWrap: "anywhere" }}>{title}</Typography>
      <Typography sx={{ mt: 1.25, maxWidth: 320, fontSize: 13, color: "#7d8791", lineHeight: 1.8, wordBreak: "keep-all", overflowWrap: "anywhere" }}>
        {active === 2 ? "거래처와 품목 정보를 확인하고 주문 초안을 작성하고 있습니다." : active === 1 ? "발주서의 텍스트와 표에서 주문 정보를 읽고 있습니다." : "접수한 발주서를 순서대로 처리하고 있습니다."}
      </Typography>
    </Box>
    <Box component="ol" aria-label="문서 처리 단계" sx={{ m: 0, p: 0, listStyle: "none", flexShrink: 0, borderTop: "1px solid #e7ebef" }}>
      {steps.map((step, index) => <Box component="li" aria-current={index === active ? "step" : undefined} key={step.title} sx={{ display: "flex", alignItems: "center", minHeight: 64, gap: 1.5, py: 1.5, borderBottom: "1px solid #edf0f2" }}>
        <Box aria-hidden="true" sx={{ width: 26, height: 26, flexShrink: 0, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 12,
          bgcolor: index === active ? "#238573" : index < active ? "#eaf4ef" : "#f3f5f6", color: index === active ? "#fff" : index < active ? "#238573" : "#8d98a2" }}>
          {index < active ? <CheckRounded sx={{ fontSize: 16 }} /> : index + 1}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, color: index <= active ? "#34434d" : "#87929c" }}>{step.title}</Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12, color: "#8a949d", lineHeight: 1.6 }}>{step.detail}</Typography>
        </Box>
        <Typography sx={{ flexShrink: 0, fontSize: 11, color: index === active ? "#147d70" : "#8a949d" }}>
          {index < active ? "완료" : index === active ? active === 0 ? "접수 완료" : "진행 중" : "대기"}
        </Typography>
      </Box>)}
    </Box>
    <Typography sx={{ pt: 2, fontSize: 12, color: "#7d8791", lineHeight: 1.7, textAlign: "center" }}>창을 닫아도 작업은 계속됩니다.</Typography>
  </Box>;
}

function ItemCell({ label, field, onChange, onEvidence }) {
  const warning = Boolean(field?.review_required);
  const centered = /수량|단위|납기/.test(label);
  const reasons = (field?.review_reasons || []).filter(reason => reason !== "USER_CONFIRMED").map(reason => reasonLabels[reason] || reason).join(" · ");
  return <Box sx={{ position: "relative", minWidth: 0, "& .cell-evidence": { opacity: warning ? 1 : 0 }, "&:hover .cell-evidence, &:focus-within .cell-evidence": { opacity: 1 } }}>
    <TextField fullWidth size="small" value={field?.value ?? ""} onChange={event => onChange(event.target.value)}
      inputProps={{ "aria-label": label, title: warning ? reasons || "확인 필요" : String(field?.value ?? ""), inputMode: label.includes("수량") ? "decimal" : "text" }}
      multiline={label.includes("품목명") || label.includes("규격")} maxRows={3}
      sx={{ "& .MuiInputBase-root": { minHeight: 36, fontSize: 13, bgcolor: warning ? "#fff9ed" : "transparent", borderRadius: 1 }, "& .MuiOutlinedInput-input": { pl: centered ? 2.5 : 1, pr: 2.5, py: 1, textAlign: centered ? "center" : "left", fontVariantNumeric: "tabular-nums" }, "& .MuiInputBase-multiline": { p: 0 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: warning ? "#e8d6ad" : "transparent" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd3da" } }} />
    <Tooltip title={warning ? `확인 필요 · ${reasons || "원문 확인"}` : "원문 근거"}>
      <IconButton className="cell-evidence" size="small" aria-label={`${label} 원문 근거`} onClick={() => onEvidence({ label, field })} sx={{ position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)", color: warning ? "#a76d08" : "#87919b", p: 0.25 }}><FindInPageOutlined sx={{ fontSize: 15 }} /></IconButton>
    </Tooltip>
  </Box>;
}

function ReviewField({ label, field, onChange, onEvidence }) {
  const reasons = (field?.review_reasons || []).filter(reason => reason !== "USER_CONFIRMED");
  return <Stack spacing={0.5} sx={{ minWidth: 0 }}>
    <Typography component="label" sx={{ fontSize: 12, color: "#697582", fontWeight: 500 }}>{label}</Typography>
    <TextField size="small" fullWidth value={field?.value ?? ""} inputProps={{ "aria-label": label }}
      multiline={label === "비고"} minRows={1} maxRows={2} onChange={e => onChange(e.target.value)}
      InputProps={{ endAdornment: <Tooltip title={field?.review_required ? `확인 필요 · ${reasons.map(reason => reasonLabels[reason] || reason).join(", ")}` : "원문 근거"}><IconButton size="small" aria-label={`${label} 원문 근거`} onClick={() => onEvidence({ label, field })}><FindInPageOutlined sx={{ fontSize: 16, color: field?.review_required ? "#ad7921" : "#a1aab3" }} /></IconButton></Tooltip> }}
      sx={{ "& .MuiInputBase-root": { minHeight: 38, fontSize: 13, borderRadius: 1, bgcolor: field?.review_required ? "#fffaf0" : "#f7f9fa" }, "& .MuiInputBase-multiline": { py: 0, pl: 0 }, "& .MuiOutlinedInput-input": { py: 1, px: 1.25, lineHeight: "20px", boxSizing: "content-box" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: field?.review_required ? "#e8d6ad" : "#e8edf0" } }} />
  </Stack>;
}

export function OssAiUploadDialog({ open, onClose, groupCode, projectCode, onCreated, onOpenOrder }) {
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [more, setMore] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [draft, setDraft] = useState(null);
  const [orderIndex, setOrderIndex] = useState(0);
  const [source, setSource] = useState("");
  const [sourceExpanded, setSourceExpanded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [jobLoadFailed, setJobLoadFailed] = useState(false);
  const [notice, setNotice] = useState("");
  const [evidence, setEvidence] = useState(null);
  const [discard, setDiscard] = useState(null);
  const [catalogMatches, setCatalogMatches] = useState({});
  const [matching, setMatching] = useState(false);
  const [completedMatchKey, setCompletedMatchKey] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [registrationState, setRegistrationState] = useState({ available: false, items: [] });
  const [confirmRegistration, setConfirmRegistration] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const uploadInput = useRef(null);
  const lock = useRef(false);
  const scope = `${groupCode}/${projectCode}`;
  const previousScope = useRef(scope);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCatalog([]);
    fetchOssStandards(groupCode, projectCode).then(data => {
      if (!cancelled) setCatalog((data.items || []).filter(item => Number(item.is_active) === 1));
    }).catch(e => { if (!cancelled) setError(errorText(e)); });
    return () => { cancelled = true; };
  }, [open, groupCode, projectCode]);

  useEffect(() => {
    if (previousScope.current !== scope) {
      previousScope.current = scope;
      setJobId(null); setJob(null); setDraft(null); setDirty(false); setOffset(0); setCatalogMatches({});
    }
  }, [scope]);

  useEffect(() => {
    if (!open || jobId) return undefined;
    let cancelled = false;
    setBusy(true); setError("");
    listAiJobs(groupCode, projectCode, offset).then(data => {
      if (!cancelled) { setItems(data.items); setMore(data.has_more); }
    }).catch(e => { if (!cancelled) setError(errorText(e)); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [open, jobId, groupCode, projectCode, offset, refresh]);

  useEffect(() => {
    if (!open || !jobId) return undefined;
    let cancelled = false;
    let timer;
    let objectUrl;
    setJobLoadFailed(false);
    setCompletedMatchKey("");
    setRegistrationState({ available: false, items: [] }); setConfirmRegistration(false); setConfirmed(false);
    setJob(null); setDraft(null); setSource(""); setSourceExpanded(false); setEvidence(null); setOrderIndex(0); setDirty(false); setError(""); setCatalogMatches({});
    readAiSource(groupCode, projectCode, jobId).then(blob => {
      if (!cancelled) { objectUrl = URL.createObjectURL(blob); setSource(objectUrl); }
    }).catch(e => { if (!cancelled) setError(errorText(e)); });
    const poll = async () => {
      try {
        const current = await readAiJob(groupCode, projectCode, jobId);
        if (cancelled) return;
        setJob(current);
        if (current.status === "REVIEW_REQUIRED") {
          const result = await readAiResult(groupCode, projectCode, jobId);
          if (cancelled) return;
          setJob(prev => ({ ...prev, version: result.version || current.version }));
          setDraft(result.reviewed_result || result.original_result);
          try {
            const registrations = await readAiRegistrations(groupCode, projectCode, jobId);
            if (!cancelled) {
              setRegistrationState(registrations);
              setDraft(prev => ({ ...prev, orders: prev.orders.map((order, index) =>
                registrations.items.find(entry => entry.draft_index === index)?.reviewed_order || order) }));
            }
          } catch (e) { if (!cancelled) setRegistrationState({ available: false, items: [], message: errorText(e) }); }
        } else if (running(current.status)) timer = setTimeout(poll, 4000);
      } catch (e) { if (!cancelled) { setJobLoadFailed(true); setError(errorText(e)); } }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [open, jobId, groupCode, projectCode, refresh]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const leave = action => {
    if (lock.current) return;
    if (dirty) setDiscard(() => action); else action();
  };
  const perform = async action => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError(""); setNotice("");
    try { await action(); } catch (e) { setError(errorText(e)); }
    finally { lock.current = false; setBusy(false); }
  };
  const upload = file => {
    if (!file) return;
    if (!/\.(pdf|png|jpe?g)$/i.test(file.name) || file.size > 20 * 1024 * 1024 || !file.size) {
      setError("20MB 이하의 PDF, PNG, JPEG 파일을 선택해주세요."); return;
    }
    perform(async () => { const created = await uploadAiFile(groupCode, projectCode, file); setJobId(created.job_id); });
  };
  const edit = (key, value, itemIndex = null) => {
    setDraft(prev => ({ ...prev, orders: prev.orders.map((order, i) => i !== orderIndex ? order : itemIndex === null
      ? { ...order, [key]: { ...order[key], value: value || null, review_required: false, review_reasons: ["USER_CONFIRMED"] } }
      : { ...order, items: order.items.map((item, j) => j !== itemIndex ? item : { ...item, [key]: { ...item[key], value: value || null, review_required: false, review_reasons: ["USER_CONFIRMED"] } }) }) }));
    setDirty(true); setNotice("");
    if (itemIndex !== null && ["name", "specification"].includes(key)) {
      setMatching(true);
      setDraft(prev => ({ ...prev, orders: prev.orders.map((order, index) => index !== orderIndex ? order : {
        ...order, items: order.items.map((item, i) => i !== itemIndex ? item : { ...item, catalog_item_id: null }),
      }) }));
    }
  };
  const selected = draft?.orders?.[orderIndex];
  const registered = registrationState.items.find(entry => entry.draft_index === orderIndex);
  const matchSignature = JSON.stringify((selected?.items || []).map(item => ({
    key: item.item_id,
    name: item.name?.value || "",
    specification: item.specification?.value || "",
  })));
  const matchKey = `${jobId}:${orderIndex}:${matchSignature}`;
  const matchingPending = Boolean(selected?.items?.length && !registered && (matching || completedMatchKey !== matchKey));

  useEffect(() => {
    if (!open || !selected?.items?.length || registered) { setCatalogMatches({}); setMatching(false); return undefined; }
    let cancelled = false;
    setMatching(true);
    setCatalogMatches({});
    const timer = setTimeout(async () => {
      setMatching(true);
      try {
        const matches = await matchAiItems(groupCode, projectCode, JSON.parse(matchSignature));
        if (!cancelled) setCatalogMatches(Object.fromEntries(matches.map(match => {
          const stored = selected.items.find(item => item.item_id === match.key)?.catalog_item_id;
          return [match.key, { ...match, selectedItemId: stored || match.selectedItemId,
            status: stored ? "USER_MATCHED" : match.status }];
        })));
      } catch (e) {
        if (!cancelled) setError(errorText(e));
      } finally {
        if (!cancelled) { setMatching(false); setCompletedMatchKey(matchKey); }
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  // Catalogue selection itself must not trigger another matching request.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, groupCode, projectCode, matchKey, registered]);

  const chooseCatalogItem = (key, itemId) => {
    setCatalogMatches(prev => ({ ...prev, [key]: { ...prev[key], selectedItemId: itemId ? String(itemId) : null,
      status: itemId ? "USER_MATCHED" : prev[key]?.candidates?.length ? "CANDIDATES" : "UNMATCHED" } }));
    setDraft(prev => ({ ...prev, orders: prev.orders.map((order, index) => index !== orderIndex ? order : {
      ...order, items: order.items.map(item => item.item_id === key ? { ...item, catalog_item_id: itemId ? String(itemId) : null } : item),
    }) }));
    setDirty(true);
  };
  const reviewSummary = (() => {
    let total = 0; let required = 0;
    (draft?.orders || []).forEach(order => {
      Object.entries(order).forEach(([key, field]) => { if (key !== "items" && field && typeof field === "object" && "review_required" in field) { total += 1; required += Number(Boolean(field.review_required)); } });
      order.items.forEach(item => Object.keys(itemFields).forEach(key => { total += 1; required += Number(Boolean(item[key]?.review_required)); }));
    });
    return { total, required, clear: total - required };
  })();
  const updateItems = transform => {
    setDraft(prev => ({ ...prev, orders: prev.orders.map((order, i) => i === orderIndex ? { ...order, items: transform(order.items) } : order) }));
    setDirty(true); setNotice("");
  };
  const addItem = () => updateItems(rows => [...rows, {
    item_id: crypto.randomUUID(), page: rows[rows.length - 1]?.page || 1, row_number: "추가",
    ...Object.fromEntries(Object.keys(itemFields).map(key => [key, { value: null, raw_value: null, review_required: true, review_reasons: [], evidence: [] }])),
  }]);
  const withCatalogMatches = () => ({ ...draft, orders: draft.orders.map((order, index) =>
    index !== orderIndex || registered ? order : { ...order, items: order.items.map(item => ({ ...item,
      catalog_item_id: catalogMatches[item.item_id]?.selectedItemId
        ? String(catalogMatches[item.item_id].selectedItemId) : item.catalog_item_id || null,
    })) }) });
  const saveDraft = async () => {
    const next = withCatalogMatches();
    const saved = await saveAiReview(groupCode, projectCode, jobId, job.version, next);
    setDraft(next);
    setJob(prev => ({ ...prev, version: saved.version })); setDirty(false); setNotice("검토 내용을 저장했습니다.");
    return saved.version;
  };
  const save = () => perform(saveDraft);
  const unmatched = (selected?.items || []).filter(item => !(catalogMatches[item.item_id]?.selectedItemId || item.catalog_item_id)).length;
  const register = () => perform(async () => {
    setConfirmRegistration(false);
    // Reconcile first: a previous response may have been lost after COMMIT.
    const current = await readAiRegistrations(groupCode, projectCode, jobId);
    setRegistrationState(current);
    const previous = current.items.find(entry => entry.draft_index === orderIndex);
    if (previous) {
      setDirty(false); setNotice(`이미 등록된 주문입니다. ${previous.orderNo}`);
      setDraft(prev => ({ ...prev, orders: prev.orders.map((order, index) => index === orderIndex ? previous.reviewed_order : order) }));
      onCreated?.(previous); return;
    }
    const version = await saveDraft();
    const created = await registerAiOrder(groupCode, projectCode, jobId, version, orderIndex);
    setRegistrationState(prev => ({ ...prev, items: [...prev.items, created] }));
    setDraft(prev => ({ ...prev, orders: prev.orders.map((order, index) => index === orderIndex ? created.reviewed_order : order) }));
    setNotice(`${created.alreadyRegistered ? "이미 등록된 주문입니다." : "주문을 등록했습니다."} ${created.orderNo}`);
    onCreated?.(created);
  });

  return <>
    <Dialog open={open} onClose={() => leave(onClose)} maxWidth={false} fullWidth
      PaperProps={{ sx: { width: jobId ? "calc(100vw - 40px)" : 900, maxWidth: jobId ? 1920 : "calc(100vw - 48px)", height: jobId ? "calc(100dvh - 40px)" : "min(850px, calc(100dvh - 64px))", maxHeight: "calc(100dvh - 40px)", m: 2.5, borderRadius: 2 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #e2e6ec", px: "20px !important", py: "14px !important", minHeight: 64 }}>
        {jobId && <Tooltip title="작업 목록"><IconButton disabled={busy} onClick={() => leave(() => { setJobId(null); setNotice(""); })}><ArrowBackRounded /></IconButton></Tooltip>}
        <Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontWeight: 700, fontSize: 16, color: "#26313b" }}>{draft ? "발주서 검토" : "AI 발주서 업로드"}</Typography>
          {job && <Typography variant="caption" noWrap component="div">{job.filename}</Typography>}</Box>
        {job && <Chip size="small" label={registered ? "주문 등록 완료" : statuses[job.status] || job.status} color={registered ? "success" : job.status === "FAILED" ? "error" : "default"} />}
        <Tooltip title="닫기"><IconButton disabled={busy} onClick={() => leave(onClose)}><CloseRounded /></IconButton></Tooltip>
      </DialogTitle>
      {busy && <LinearProgress />}
      <DialogContent sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, minHeight: 0 }}>
        {error && <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => leave(() => setRefresh(x => x + 1))}>다시 조회</Button>}>{error}</Alert>}
        {notice && <Alert severity="success">{notice}</Alert>}
        {!jobId ? <>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={700}>업로드 작업</Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="새로고침"><IconButton disabled={busy} onClick={() => setRefresh(x => x + 1)}><RefreshRounded /></IconButton></Tooltip>
              <Button disabled={busy} variant="contained" startIcon={<UploadFileRounded />} onClick={() => uploadInput.current?.click()}>발주서 선택</Button>
            </Stack>
          </Stack>
          <input ref={uploadInput} hidden type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          <Typography variant="caption" color="text.secondary">PDF · PNG · JPEG / 최대 20MB</Typography>
          <Box sx={{ overflow: "auto", flex: 1 }}><Table size="small" stickyHeader>
            <TableHead><TableRow><TableCell>파일명</TableCell><TableCell>상태</TableCell><TableCell>업로드 일시</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>{items.map(item => <TableRow key={item.job_id} hover>
              <TableCell sx={{ maxWidth: 350, overflowWrap: "anywhere" }}>{item.filename}</TableCell>
              <TableCell>{statuses[item.status] || item.status}</TableCell>
              <TableCell>{new Date(`${item.created_at}${/Z$|[+-]\d\d:\d\d$/.test(item.created_at) ? "" : "Z"}`).toLocaleString("ko-KR")}</TableCell>
              <TableCell><Button disabled={busy} onClick={() => setJobId(item.job_id)}>열기</Button></TableCell>
            </TableRow>)}</TableBody>
          </Table>{!items.length && !busy && <Typography color="text.secondary" sx={{ py: 5, textAlign: "center" }}>업로드한 발주서가 없습니다.</Typography>}</Box>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button disabled={busy || offset === 0} onClick={() => setOffset(x => Math.max(0, x - 20))}>이전</Button>
            <Button disabled={busy || !more} onClick={() => setOffset(x => x + 20)}>다음</Button>
          </Stack>
        </> : <Box sx={{ display: "grid", gridTemplateColumns: sourceExpanded ? "minmax(0, 1fr)" : { xs: "minmax(0, 1fr)", md: draft ? "minmax(0, 0.8fr) minmax(0, 1.2fr)" : "minmax(0, 1.8fr) minmax(360px, 0.85fr)" }, gridTemplateRows: { xs: sourceExpanded ? "minmax(0, 1fr)" : "minmax(320px, 1fr) minmax(240px, 1fr)", md: "minmax(0, 1fr)" }, gap: 3, minHeight: 0, flex: 1 }}>
          <Box sx={{ minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DescriptionOutlined sx={{ fontSize: 19, color: "#68717e" }} /><Typography fontWeight={700} sx={{ flex: 1 }}>발주서 원본</Typography>
              <Tooltip title={sourceExpanded ? "분할 보기" : "원본 크게 보기"}><IconButton size="small" aria-label={sourceExpanded ? "분할 보기" : "원본 크게 보기"} onClick={() => setSourceExpanded(value => !value)}>{sourceExpanded ? <CloseFullscreenRounded fontSize="small" /> : <OpenInFullRounded fontSize="small" />}</IconButton></Tooltip>
            </Stack>
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", bgcolor: "#eef0f3", border: "1px solid #e2e6ec", borderRadius: 1 }}>
              {source && (job?.media_type === "application/pdf" ? <iframe title="발주서 원본" src={`${source}#view=FitH&navpanes=0`} style={{ width: "100%", height: "100%", display: "block", border: 0 }} />
                : job && <img alt="발주서 원본" src={source} style={{ width: "100%", display: "block" }} />)}
            </Box>
          </Box>
          <Stack spacing={2} sx={{ display: sourceExpanded ? "none" : "flex", overflow: "auto", minWidth: 0, pr: 0.5 }}>
            {jobLoadFailed ? <Box sx={{ px: 3, py: 5 }}>
              <Typography fontSize={20} fontWeight={700}>작업 상태를 불러오지 못했습니다</Typography>
              <Typography sx={{ mt: 1, mb: 2, color: "text.secondary", fontSize: 14 }}>서버 연결을 확인한 뒤 다시 조회해주세요. 현재 처리 상태는 확인되지 않았습니다.</Typography>
              <Button startIcon={<RefreshRounded />} onClick={() => leave(() => setRefresh(x => x + 1))}>다시 조회</Button>
            </Box> : (!job || running(job.status)) && <ProcessingStatus job={job} />}
            {job?.status === "FAILED" && <Alert severity="error" action={<Button disabled={busy} onClick={() => perform(async () => { await retryAiJob(groupCode, projectCode, jobId); setRefresh(x => x + 1); })}>재시도</Button>}>처리에 실패했습니다. {job.error_code}</Alert>}
            {draft && <>
              {registrationState.message && <Alert severity="warning">{registrationState.message}</Alert>}
              {registered && <Alert severity="success" action={onOpenOrder && <Button color="inherit" size="small" onClick={() => leave(() => onOpenOrder(registered.orderId))}>주문 보기</Button>}>등록 완료 · {registered.orderNo}</Alert>}
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ py: 1.5, borderBottom: "1px solid #e5eaee" }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#26313b" }}>추출 결과</Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography sx={{ fontSize: 12, color: "#78838e" }}>전체 {reviewSummary.total}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#33816c" }}>일치·확인 완료 {reviewSummary.clear}</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: reviewSummary.required ? "#9b6b1d" : "#33816c", bgcolor: reviewSummary.required ? "#fbf3e3" : "#edf6f1", px: 1.25, py: 0.5, borderRadius: 1 }}>확인 필요 {reviewSummary.required}</Typography>
                </Stack>
              </Stack>
              {draft.warnings?.length > 0 && <Box component="details" sx={{ fontSize: 12, color: "#687582", "& summary": { cursor: "pointer", py: 0.5 }, "& p": { my: 0.75, lineHeight: 1.7 } }}>
                <Box component="summary">문서 주의사항 {draft.warnings.length}건</Box>
                {draft.warnings.map((warning, index) => <Typography component="p" key={index} fontSize={12}>{warning === "Pages are separate drafts; confirm order grouping before registration." ? "페이지별로 주문 초안이 나뉘어 있습니다. 등록 전 주문 묶음을 확인해주세요." : warning}</Typography>)}
              </Box>}
              {!draft.orders?.length && <Alert severity="warning">추출된 주문이 없습니다. 원본을 확인해주세요.</Alert>}
              {draft.orders?.length > 1 && <TextField disabled={busy} select size="small" label="주문 초안" value={orderIndex} onChange={e => {
                setDraft(withCatalogMatches()); setOrderIndex(Number(e.target.value)); setEvidence(null);
              }}>
                {draft.orders.map((order, i) => <MenuItem key={i} value={i}>초안 {i + 1} · {order.customer_po_no.value || order.customer_name.value || "미확인"}{registrationState.items.some(entry => entry.draft_index === i) ? " · 등록 완료" : ""}</MenuItem>)}
              </TextField>}
              {selected && <Box component="fieldset" disabled={busy || Boolean(registered)} sx={{ border: 0, p: 0, m: 0, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#34404b" }}>주문 정보</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", xl: "1fr 1fr 1fr" }, columnGap: 2, rowGap: 1.5 }}>
                  {Object.entries(headerFields).map(([key, label]) => <ReviewField key={key} label={label} field={selected[key]} onChange={v => edit(key, v)} onEvidence={setEvidence} />)}
                </Box>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#34404b" }}>주문 품목 <Box component="span" sx={{ ml: 1, color: "#7b8791", fontWeight: 500 }}>{selected.items.length}</Box></Typography>
                  <Button size="small" startIcon={<AddRounded />} onClick={addItem} sx={{ color: "#465561" }}>품목 추가</Button>
                </Stack>
                <Box sx={{ overflowX: "auto", border: "1px solid #e2e6ec", borderRadius: 1 }}>
                  <Table size="small" aria-label="발주서 품목 편집" sx={{ minWidth: 900, tableLayout: "fixed", "& th": { textAlign: "center", height: 40, boxSizing: "border-box", bgcolor: "#f5f7f9", color: "#687582", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }, "& td, & th": { px: 0.75, py: 1 }, "& td": { height: 66, boxSizing: "border-box", borderColor: "#edf0f2", verticalAlign: "middle" }, "& tr:last-child td": { borderBottom: 0 } }}>
                    <TableHead><TableRow>
                      {[['No.', '4%'], ['기준 품목', '17%'], ['품목명', '28%'], ['규격', '16%'], ['수량', '8%'], ['단위', '8%'], ['납기', '15%'], ['', '4%']].map(([label, width], index) => <TableCell key={index} sx={{ width }}>{label}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>{selected.items.map((item, i) => {
                    const match = catalogMatches[item.item_id];
                    const selectedId = match?.selectedItemId || item.catalog_item_id;
                    const matched = catalog.find(candidate => String(candidate.item_id) === String(selectedId));
                    return <TableRow key={item.item_id} hover>
                      <TableCell align="center"><Tooltip title={item.row_number === "추가" ? "직접 추가" : `원본 ${item.page}페이지 · ${item.row_number}행`}><Typography fontSize={12} color="text.secondary">{i + 1}</Typography></Tooltip></TableCell>
                      <TableCell>
                      <Autocomplete size="small" options={catalog} value={matched || null}
                        disabled={matchingPending || busy || Boolean(registered)}
                        getOptionLabel={option => `${option.item_code} · ${option.item_name}${option.specification ? ` · ${option.specification}` : ""}`}
                        isOptionEqualToValue={(option, value) => String(option.item_id) === String(value.item_id)}
                        onChange={(_, value) => chooseCatalogItem(item.item_id, value?.item_id)}
                        noOptionsText="등록된 기준 품목이 없습니다"
                        renderInput={params => <TextField {...params} placeholder={matchingPending ? "매칭 중" : "기준 품목 검색"}
                          inputProps={{ ...params.inputProps, "aria-label": `${i + 1}행 기준 품목` }} />}
                        sx={{ "& .MuiInputBase-root": { fontSize: 12 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e9ed" } }} />
                      <Tooltip title={matched ? `단위 ${matched.unit_code || '-'} · 소요일 ${matched.standard_lead_days ?? '기본값'} · 버퍼 ${matched.planning_buffer_days ?? '기본값'}` : "기준정보 연결 확인 필요"}>
                        <Typography sx={{ fontSize: 11, mt: 0.5 }} color={matched ? "success.main" : "warning.main"}>{matched ? match?.status === "AUTO_MATCHED" ? "자동 매칭" : "선택 완료" : matchingPending ? "매칭 중" : match?.status === "UNMATCHED" ? "미매칭" : "후보 확인"}</Typography>
                      </Tooltip>
                      </TableCell>
                      {Object.entries(itemFields).map(([key, label]) => <TableCell key={key}><ItemCell label={`${i + 1}행 ${label}`} field={item[key]} onChange={v => edit(key, v, i)} onEvidence={setEvidence} /></TableCell>)}
                      <TableCell><Tooltip title="품목 삭제"><IconButton size="small" aria-label={`${i + 1}행 품목 삭제`} onClick={() => updateItems(rows => rows.filter((_, j) => j !== i))}><DeleteOutlineRounded sx={{ fontSize: 18 }} /></IconButton></Tooltip></TableCell>
                    </TableRow>;
                    })}</TableBody>
                  </Table>
                </Box>
              </Box>}
            </>}
          </Stack>
        </Box>}
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid #e2e6ec", px: 2, py: 1.5 }}>
        {dirty && <Typography variant="caption" color="warning.main" sx={{ mr: "auto" }}>저장하지 않은 변경사항</Typography>}
        <Button disabled={busy} onClick={() => leave(onClose)}>닫기</Button>
        {draft && jobId && <Button variant="outlined" startIcon={<SaveRounded />} disabled={busy || matchingPending || Boolean(registered)} onClick={save}>검토 내용 저장</Button>}
        {selected && !registered && <Button variant="contained" startIcon={<CheckRounded />}
          disabled={busy || matchingPending || !registrationState.available} onClick={() => { setConfirmed(false); setConfirmRegistration(true); }}>확인 후 주문 등록</Button>}
      </DialogActions>
    </Dialog>
    <Dialog open={confirmRegistration} onClose={() => !busy && setConfirmRegistration(false)} maxWidth="sm" fullWidth>
      <DialogTitle>주문 등록 확인</DialogTitle>
      <DialogContent><Stack spacing={2}>
        <Typography>초안 {orderIndex + 1} · {selected?.customer_name?.value || "거래처 미입력"} · 품목 {selected?.items?.length || 0}건</Typography>
        <Typography variant="body2" color="text.secondary">현재 선택한 초안을 별도 주문 한 건으로 접수합니다. 품목 연결, 일정 및 원본 발주서가 함께 저장됩니다.</Typography>
        {unmatched > 0 && <Alert severity="warning">기준 품목이 선택되지 않은 행이 {unmatched}개 있습니다.</Alert>}
        <FormControlLabel control={<Checkbox checked={confirmed} onChange={event => setConfirmed(event.target.checked)} />}
          label="원본과 거래처, 품목, 수량, 납기 및 주문 구분을 확인했습니다." />
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setConfirmRegistration(false)}>계속 검토</Button>
        <Button variant="contained" startIcon={<CheckRounded />} disabled={busy || matchingPending || !confirmed || unmatched > 0 || !selected?.items?.length} onClick={register}>주문 등록</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={Boolean(evidence)} onClose={() => setEvidence(null)} maxWidth="sm" fullWidth>
      <DialogTitle>{evidence?.label} 원문 근거</DialogTitle>
      <DialogContent><Stack spacing={2}>
        <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{evidence?.field?.raw_value || "원문 값 없음"}</Typography>
        {evidence?.field?.review_reasons?.map((reason, i) => <Typography key={i} color="warning.main">{reason}</Typography>)}
        {evidence?.field?.evidence?.map((entry, i) => <Box key={i}><Typography variant="caption">{entry.page}페이지</Typography><Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{entry.raw_text}</Typography></Box>)}
      </Stack></DialogContent><DialogActions><Button onClick={() => setEvidence(null)}>닫기</Button></DialogActions>
    </Dialog>
    <Dialog open={Boolean(discard)} onClose={() => setDiscard(null)}>
      <DialogTitle>저장하지 않은 수정 내용이 있습니다.</DialogTitle>
      <DialogContent>이동하면 이번 수정 내용은 사라집니다.</DialogContent>
      <DialogActions><Button onClick={() => setDiscard(null)}>계속 검토</Button><Button color="error" onClick={() => { const action = discard; setDiscard(null); setDirty(false); action(); }}>저장하지 않고 이동</Button></DialogActions>
    </Dialog>
  </>;
}
